/**
 * [PDF-TEMPLATE] Client helper — builds section payloads and drives the
 * background report-export job (POST /reports/export-pdf|xlsx → poll status
 * → download result). Used by FormSubmissionsPage and module submission
 * exports. See app/tasks/report_tasks.py on the backend for why this is a
 * Celery job instead of a synchronous request.
 */
import { apiFetch } from "../context/AppContext";

export function safeExportFilename(name) {
  return (name || "submission")
    .replace(/[^a-z0-9_-]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "submission";
}

function stringifyValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.map(stringifyValue).join(", ") : "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Normalize section payloads so the API always receives strings. */
export function normalizePdfSections(sections = []) {
  return sections.map((section) => {
    const normalized = {
      heading: stringifyValue(section.heading) || "Section",
    };

    if (section.rows?.length) {
      normalized.rows = section.rows.map((row) => ({
        label: stringifyValue(row.label),
        value: stringifyValue(row.value),
      }));
    }

    if (section.qa?.length) {
      normalized.qa = section.qa.map((item) => {
        const qa = {
          question: stringifyValue(item.question),
          answer: stringifyValue(item.answer),
        };
        if (item.note) qa.note = stringifyValue(item.note);
        if (item.owner) qa.owner = stringifyValue(item.owner);
        if (item.evidence) {
          const evidence = stringifyValue(item.evidence);
          qa.note = qa.note ? `${qa.note}\nEvidence: ${evidence}` : `Evidence: ${evidence}`;
        }
        return qa;
      });
    }

    if (section.table?.headers?.length) {
      normalized.table = {
        headers: section.table.headers.map(stringifyValue),
        rows: (section.table.rows || []).map((row) =>
          row.map(stringifyValue),
        ),
      };
    }

    if (section.metrics?.length) {
      normalized.metrics = section.metrics.map((m) => ({
        label: stringifyValue(m.label),
        value: stringifyValue(m.value),
        ...(m.color ? { color: stringifyValue(m.color) } : {}),
      }));
    }

    if (section.charts?.length) {
      normalized.charts = section.charts.map((chart) => {
        const c = {
          type: stringifyValue(chart.type),
          ...(chart.title ? { title: stringifyValue(chart.title) } : {}),
          ...(chart.suffix ? { suffix: stringifyValue(chart.suffix) } : {}),
          ...(chart.label ? { label: stringifyValue(chart.label) } : {}),
        };
        if (chart.value != null && chart.value !== "") {
          c.value = Number(String(chart.value).replace("%", "")) || 0;
        }
        if (chart.max_value != null) {
          c.max_value = Number(chart.max_value) || 100;
        }
        if (chart.labels?.length) {
          c.labels = chart.labels.map(stringifyValue);
        }
        if (chart.values?.length) {
          c.values = chart.values.map((v) => Number(v) || 0);
        }
        return c;
      });
    }

    if (section.tags?.length) {
      normalized.tags = section.tags.map((group) => ({
        label: stringifyValue(group.label),
        items: (group.items || []).map(stringifyValue).filter(Boolean),
        ...(group.color ? { color: stringifyValue(group.color) } : {}),
      }));
    }

    return normalized;
  });
}

/** Excel export — preserve all fields including empty notes/owners and question ids. */
export function normalizeExcelSections(sections = []) {
  return sections.map((section) => {
    const normalized = {
      heading: stringifyValue(section.heading) || "Section",
    };

    if (section.rows?.length) {
      normalized.rows = section.rows.map((row) => ({
        label: stringifyValue(row.label),
        value: stringifyValue(row.value),
      }));
    }

    if (section.qa?.length) {
      normalized.qa = section.qa.map((item) => {
        const qa = {
          question: stringifyValue(item.question),
          answer: stringifyValue(item.answer),
          note: stringifyValue(item.note),
          owner: stringifyValue(item.owner),
        };
        if (item.question_id != null) {
          qa.question_id = stringifyValue(item.question_id);
        }
        if (item.section) qa.section = stringifyValue(item.section);
        return qa;
      });
    }

    if (section.table?.headers?.length) {
      normalized.table = {
        headers: section.table.headers.map(stringifyValue),
        rows: (section.table.rows || []).map((row) =>
          row.map(stringifyValue),
        ),
      };
    }

    if (section.metrics?.length) {
      normalized.metrics = section.metrics.map((m) => ({
        label: stringifyValue(m.label),
        value: stringifyValue(m.value),
        ...(m.color ? { color: stringifyValue(m.color) } : {}),
      }));
    }

    if (section.charts?.length) {
      normalized.charts = section.charts.map((chart) => {
        const c = {
          type: stringifyValue(chart.type),
          ...(chart.title ? { title: stringifyValue(chart.title) } : {}),
          ...(chart.suffix ? { suffix: stringifyValue(chart.suffix) } : {}),
          ...(chart.label ? { label: stringifyValue(chart.label) } : {}),
        };
        if (chart.value != null && chart.value !== "") {
          c.value = Number(String(chart.value).replace("%", "")) || 0;
        }
        if (chart.max_value != null) {
          c.max_value = Number(chart.max_value) || 100;
        }
        if (chart.labels?.length) {
          c.labels = chart.labels.map(stringifyValue);
        }
        if (chart.values?.length) {
          c.values = chart.values.map((v) => Number(v) || 0);
        }
        return c;
      });
    }

    if (section.tags?.length) {
      const tagRows = section.tags.map((group) => ({
        label: stringifyValue(group.label),
        value: (group.items || []).map(stringifyValue).filter(Boolean).join(", ") || "None",
      }));
      normalized.rows = [...(normalized.rows || []), ...tagRows];
    }

    return normalized;
  });
}

function formatApiError(errBody, fallback = "Export failed") {
  if (!errBody) return fallback;
  if (typeof errBody.detail === "string") return errBody.detail;
  if (Array.isArray(errBody.detail)) {
    return errBody.detail
      .map((e) => `${(e.loc || []).join(".")}: ${e.msg}`)
      .join("; ");
  }
  return fallback;
}

async function downloadExportResponse(res, fallbackName, errorLabel) {
  if (!res.ok) {
    let detail = errorLabel;
    try {
      detail = formatApiError(await res.json(), errorLabel);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const downloadName = match?.[1] || fallbackName;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = downloadName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Report generation (ReportLab/matplotlib for PDF, openpyxl for Excel) runs
 * as a background Celery job on the backend — see app/tasks/report_tasks.py
 * — instead of blocking the request. That keeps heavy exports (e.g. the
 * "Detailed" report, which renders a chart per module) from tying up a web
 * worker or tripping a reverse-proxy timeout in production. This helper
 * kicks off the job, polls until it finishes, then returns the task id used
 * to fetch the result.
 */
async function queueAndAwaitReportJob(kind, body, { intervalMs = 1500, timeoutMs = 240000 } = {}) {
  const queueRes = await apiFetch(`/reports/export-${kind}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!queueRes.ok) {
    let detail = "Export failed";
    try {
      detail = formatApiError(await queueRes.json(), detail);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const { task_id: taskId } = await queueRes.json();

  const startedAt = Date.now();
  for (;;) {
    const statusRes = await apiFetch(`/reports/export-${kind}/status/${taskId}`);
    if (!statusRes.ok) {
      let detail = "Could not check export status";
      try {
        detail = formatApiError(await statusRes.json(), detail);
      } catch {
        // ignore
      }
      throw new Error(detail);
    }
    const statusData = await statusRes.json();
    if (statusData.status === "success") break;
    if (statusData.status === "error") {
      throw new Error(statusData.message || "Export failed");
    }
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error("Export is taking longer than expected. Please try again.");
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return taskId;
}

/**
 * Generate and download an enterprise PDF via the ReportLab backend.
 */
export async function exportSubmissionPdf({
  title,
  subtitle,
  module = "Report",
  organization,
  filename,
  sections = [],
}) {
  const safeTitle = stringifyValue(title) || "Privacy Report";

  const body = {
    title: safeTitle,
    subtitle: subtitle ? stringifyValue(subtitle) : undefined,
    module: stringifyValue(module) || "Report",
    organization: organization ? stringifyValue(organization) : undefined,
    filename: filename || `${module}_${safeExportFilename(safeTitle)}`,
    sections: normalizePdfSections(sections),
  };

  const taskId = await queueAndAwaitReportJob("pdf", body);
  const res = await apiFetch(`/reports/export-pdf/result/${taskId}`);

  await downloadExportResponse(
    res,
    `${safeExportFilename(safeTitle)}.pdf`,
    "PDF export failed",
  );
}

/**
 * Generate a PDF blob via the ReportLab backend (for inline preview).
 */
export async function generateSubmissionPdfBlob({
  title,
  subtitle,
  module = "Report",
  organization,
  sections = [],
}) {
  const safeTitle = stringifyValue(title) || "Privacy Report";

  const body = {
    title: safeTitle,
    subtitle: subtitle ? stringifyValue(subtitle) : undefined,
    module: stringifyValue(module) || "Report",
    organization: organization ? stringifyValue(organization) : undefined,
    filename: `${module}_${safeExportFilename(safeTitle)}`,
    sections: normalizePdfSections(sections),
  };

  const taskId = await queueAndAwaitReportJob("pdf", body);
  const res = await apiFetch(`/reports/export-pdf/result/${taskId}`);

  if (!res.ok) {
    let detail = "PDF export failed";
    try {
      detail = formatApiError(await res.json(), detail);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  return res.blob();
}

/**
 * Generate and download an Excel workbook via the backend.
 */
export async function exportSubmissionExcel({
  title,
  subtitle,
  module = "Report",
  organization,
  filename,
  sections = [],
}) {
  const safeTitle = stringifyValue(title) || "Privacy Report";

  const body = {
    title: safeTitle,
    subtitle: subtitle ? stringifyValue(subtitle) : undefined,
    module: stringifyValue(module) || "Report",
    organization: organization ? stringifyValue(organization) : undefined,
    filename: filename || `${module}_${safeExportFilename(safeTitle)}`,
    sections: normalizeExcelSections(sections),
  };

  const taskId = await queueAndAwaitReportJob("xlsx", body);
  const res = await apiFetch(`/reports/export-xlsx/result/${taskId}`);

  await downloadExportResponse(
    res,
    `${safeExportFilename(safeTitle)}.xlsx`,
    "Excel export failed",
  );
}
